import { useState, useRef, useEffect } from "react";
import { MagnifyingGlass, X, Camera, Lightning, ShoppingCart, Check, Barcode, CaretRight, Clock } from "@phosphor-icons/react";
import SectionLabel from "./SectionLabel";
import { T } from "../themes";
import { MEALS, MEAL_ICON } from "../constants";

export default function FoodSearchOverlay({
  isOpen, onClose,
  searchFoodsDB, fetchNLP,
  dbSuggestions, setDbSuggestions, dbSearching,
  nlpFood, setNlpFood,
  setNlpModal, setNlpModalQty, setServingPreset,
  startCameraScanner, setBarcodeStep, setLogMode,
  searchHistory, fetchSearchHistory, saveSearchQuery,
  mealType, setMealType, MEAL_COLOR,
  dbDebounceRef,
  lastLoggedItem,
  // Barcode props (Fix 1 — idle state now lives here)
  barcode, setBarcode, lookupProduct, logLoading,
  cameraErr, cameraToast, cameraSecureErr,
  cameraRationaleShown, setShowCameraRationale,
  // Barcode containment — camera view renders inside overlay
  barcodeStep, cameraActive, videoRef, videoReady, stopCamera,
  // Barcode review — product card renders inside overlay (Fix 1)
  scannedProduct, setScannedProduct,
  barcodeUnit, setBarcodeUnit,
  barcodeQty, setBarcodeQty,
  barcodeServings, setBarcodeServings,
  confirmLog,
  // OBS-001: Yesterday tab
  fetchYesterdayLogs,
  copyFromYesterday,
}) {
  const [searchMode, setSearchMode] = useState("all");
  const searchModeRef = useRef("all");
  const [addedPrompt, setAddedPrompt] = useState(null); // {name, meal}
  const [permErr, setPermErr] = useState(null); // camera permission denial message
  const inputRef = useRef(null);
  const autoCloseRef = useRef(null);
  // OBS-001: Yesterday tab state
  const [yesterdayLogs, setYesterdayLogs] = useState([]);
  const [yesterdayLoading, setYesterdayLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [copyingYesterday, setCopyingYesterday] = useState(false);

  // Keep ref in sync with state
  useEffect(() => { searchModeRef.current = searchMode; }, [searchMode]);

  // Re-trigger search when mode changes (if there's text in the input)
  useEffect(() => {
    if (isOpen && nlpFood && nlpFood.length >= 2) {
      clearTimeout(dbDebounceRef.current);
      dbDebounceRef.current = setTimeout(() => searchFoodsDB(nlpFood, searchMode), 100);
    }
    // nlpFood intentionally omitted: handleInputChange already triggers a debounced
    // search on every keystroke — adding nlpFood here would cause double-searches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchMode, isOpen]);

  // Reset to Whole foods tab and focus input on every open
  useEffect(() => {
    if (isOpen) {
      setAddedPrompt(null);
      setSearchMode("all");
      setSelectedIds(new Set());
      setYesterdayLogs([]);
      setTimeout(() => inputRef.current && inputRef.current.focus(), 300);
      if (fetchSearchHistory) fetchSearchHistory();
    }
    return () => clearTimeout(autoCloseRef.current);
  }, [isOpen]);

  // OBS-001: fetch yesterday logs when Yesterday tab becomes active
  useEffect(() => {
    if (isOpen && searchMode === "yesterday" && fetchYesterdayLogs) {
      setYesterdayLoading(true);
      setYesterdayLogs([]);
      fetchYesterdayLogs().then(data => {
        setYesterdayLogs(data || []);
        setYesterdayLoading(false);
      });
    }
  }, [searchMode, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch for successful log (lastLoggedItem changes)
  useEffect(() => {
    if (isOpen && lastLoggedItem) {
      setAddedPrompt({ name: lastLoggedItem.name, meal: mealType });
      // Auto-close after 4 seconds
      clearTimeout(autoCloseRef.current);
      autoCloseRef.current = setTimeout(() => {
        setAddedPrompt(null);
        onClose();
      }, 4000);
    }
    // onClose intentionally omitted: it is not memoised in the parent and would
    // re-run this effect on every parent render, risking premature auto-close.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastLoggedItem, isOpen]);

  if (!isOpen) return null;

  const handleInputChange = (val) => {
    setNlpFood(val);
    setServingPreset && setServingPreset("");
    clearTimeout(dbDebounceRef.current);
    if (val.length >= 2) {
      dbDebounceRef.current = setTimeout(() => searchFoodsDB(val, searchModeRef.current), 300);
    } else {
      setDbSuggestions([]);
    }
  };

  const handleSubmit = () => {
    if (!nlpFood || nlpFood.length < 2) return;
    fetchNLP();
    saveSearchQuery && saveSearchQuery(nlpFood);
  };

  // Cooking state dedup helpers
  const getBaseName = (name) => name
    .replace(/\s*[-—]\s*(raw|cooked|dried|dry|boiled|grilled|baked|fried|smoked|steamed|roasted)\s*$/gi, '')
    .replace(/\s*\((raw|cooked|dried|dry|boiled|grilled|baked|fried|smoked|steamed|roasted)\)\s*/gi, '')
    .trim();

  const getVariantLabel = (name) => {
    if (/raw/i.test(name)) return 'Raw';
    if (/cooked/i.test(name)) return 'Cooked';
    if (/grilled/i.test(name)) return 'Grilled';
    if (/baked/i.test(name)) return 'Baked';
    if (/boiled/i.test(name)) return 'Boiled';
    if (/dried|dry/i.test(name)) return 'Dried';
    if (/fried/i.test(name)) return 'Fried';
    if (/smoked/i.test(name)) return 'Smoked';
    if (/steamed/i.test(name)) return 'Steamed';
    if (/roasted/i.test(name)) return 'Roasted';
    return 'Standard';
  };

  // Deduplicate variants by cooking label — keep the one with higher protein
  const dedupeVariants = (variants) => {
    return variants.reduce((acc, v) => {
      const label = getVariantLabel(v.name);
      const existing = acc.find(a => getVariantLabel(a.name) === label);
      if (!existing) {
        acc.push(v);
      } else if ((v.p100 || 0) > (existing.p100 || 0)) {
        acc[acc.indexOf(existing)] = v;
      }
      return acc;
    }, []);
  };

  // Group results by base name — keep all variants, show one row
  const groupedResults = (() => {
    const groups = {};
    for (const row of dbSuggestions) {
      // Branded items are never grouped — they have unique products
      if (row.brand) { groups[row.name + '_' + (row.brand||'')] = { primary: row, variants: [row] }; continue; }
      const base = getBaseName(row.name).toLowerCase();
      if (!groups[base]) groups[base] = { primary: row, variants: [] };
      groups[base].variants.push(row);
    }
    // Deduplicate variants within each group
    for (const key of Object.keys(groups)) {
      if (groups[key].variants.length > 1) {
        groups[key].variants = dedupeVariants(groups[key].variants);
      }
    }
    return Object.values(groups);
  })();

  const handleSelectResult = (group) => {
    const row = group.primary;
    const variants = group.variants;
    setNlpModal({ ...row, grams: row.grams || 100, cookingVariants: variants.length > 1 ? variants : undefined });
    setNlpModalQty(String(row.grams || 100));
    setDbSuggestions([]);
    setNlpFood(getBaseName(row.name));
    onClose();
  };

  const handleHistoryTap = (query) => {
    setNlpFood(query);
    clearTimeout(dbDebounceRef.current);
    dbDebounceRef.current = setTimeout(() => searchFoodsDB(query, searchModeRef.current), 100);
  };

  // Barcode: launch camera scanner — stays inside overlay now
  // Permission is checked BEFORE opening scanner UI so the browser dialog
  // appears on a clean screen (not hidden behind the overlay).
  const handleOpenScanner = async () => {
    if (!cameraRationaleShown) { setShowCameraRationale(true); return; }
    // Probe for camera permission first — dialog appears on clean screen
    if (navigator.mediaDevices?.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach(t => t.stop()); // release; scanner opens its own
      } catch {
        setPermErr("Camera access is needed to scan barcodes. Please enable it in your device settings.");
        return; // do not open scanner
      }
    }
    setPermErr(null);
    if (setLogMode) setLogMode("barcode");
    if (setBarcodeStep) setBarcodeStep("scanning");
    startCameraScanner();
    // onClose() intentionally removed — scanner renders inside overlay
  };

  // Barcode: manual entry lookup
  const handleManualBarcode = () => {
    if (barcode && barcode.trim()) {
      if (setLogMode) setLogMode("barcode");
      lookupProduct(barcode.trim());
      onClose();
    }
  };

  const handleAddAnother = () => {
    clearTimeout(autoCloseRef.current);
    setAddedPrompt(null);
    setNlpFood("");
    setDbSuggestions([]);
    if (inputRef.current) setTimeout(() => inputRef.current.focus(), 300);
  };

  const handleDone = () => {
    clearTimeout(autoCloseRef.current);
    setAddedPrompt(null);
    onClose();
  };

  // OBS-001: Yesterday tab handlers
  const toggleYesterdayItem = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const handleSelectAllYesterday = () => {
    if (selectedIds.size === yesterdayLogs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(yesterdayLogs.map(l => l.id)));
    }
  };
  const handleCopySelected = async () => {
    if (!copyFromYesterday || selectedIds.size === 0) return;
    setCopyingYesterday(true);
    const items = yesterdayLogs.filter(l => selectedIds.has(l.id));
    const ok = await copyFromYesterday(items);
    setCopyingYesterday(false);
    if (ok) { setSelectedIds(new Set()); onClose(); }
  };

  const handleSetSearchMode = (id) => {
    if (id !== "barcode" && (barcodeStep === "scanning" || cameraActive)) {
      stopCamera && stopCamera();
      setBarcodeStep && setBarcodeStep("idle");
    }
    if (id !== "barcode") setPermErr(null);
    if (id !== "yesterday") setSelectedIds(new Set());
    setSearchMode(id);
  };

  const modes = [
    { id: "all",       label: "Whole foods", icon: <Lightning size={14} weight="fill" /> },
    { id: "branded",   label: "Branded",     icon: <ShoppingCart size={14} weight="bold" /> },
    { id: "barcode",   label: "Barcode",     icon: <Barcode size={14} weight="bold" /> },
    { id: "yesterday", label: "Yesterday",   icon: <Clock size={14} weight="bold" /> },
  ];

  const recentItems = (searchHistory || []).slice(0, 8);
  const showRecent = !nlpFood && recentItems.length > 0 && dbSuggestions.length === 0 && !addedPrompt && searchMode !== "barcode" && searchMode !== "yesterday";
  const showSearch = searchMode !== "barcode" && searchMode !== "yesterday" && !addedPrompt;

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      zIndex: 2000, background: T.pageBg || T.bg,
      display: "flex", flexDirection: "column",
      fontFamily: T.fontSans,
    }}>
      {/* ── Header ─────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", padding: "16px 16px 12px",
        background: T.pageBg || T.bg,
      }}>
        <button onClick={onClose} style={{
          background: T.surfaceLight || T.surface, border: "none", cursor: "pointer",
          width: 36, height: 36, borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <X size={18} weight="bold" color={T.inkMuted || T.textSub} />
        </button>
        <span style={{
          flex: 1, textAlign: "center", fontSize: 18, fontWeight: 700,
          fontFamily: T.fontDisplay, color: T.text, marginRight: 36,
        }}>Log food</span>
      </div>

      {/* ── Meal Type Selector (pills) ─────────────────────── */}
      <div style={{ display: "flex", gap: 6, padding: "0 16px 10px" }}>
        {MEALS.map(mt => {
          const active = mealType === mt;
          const mealCol = MEAL_COLOR?.[mt] || T.accent;
          return (
            <button key={mt} onClick={() => setMealType(mt)} style={{
              flex: 1, padding: "9px 4px",
              background: active ? mealCol : "transparent",
              border: active ? "none" : `1.5px solid ${T.surfaceMid || T.surface}`,
              borderRadius: 20,
              color: active ? "#fff" : (T.inkMuted || T.textDim),
              cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: T.fontSans,
              letterSpacing: "0.2px",
              transition: "all 0.15s",
            }}>
              {mt}
            </button>
          );
        })}
      </div>

      {/* ── Search Type Tabs ──────────────────────────────── */}
      <div style={{ display: "flex", gap: 6, padding: "0 16px 12px" }}>
        {modes.map(m => {
          const active = searchMode === m.id;
          return (
            <button key={m.id} onClick={() => handleSetSearchMode(m.id)} style={{
              flex: 1, padding: "10px 6px", cursor: "pointer",
              background: active ? T.tileBg : "transparent",
              border: active ? `1px solid ${T.tileBg}` : `1px solid ${T.cardBorder}`,
              borderRadius: 12,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              transition: "all 0.15s",
            }}>
              <span style={{ color: active ? T.tileAccent : (T.inkFaint || T.textDim), display: "flex" }}>
                {m.icon}
              </span>
              <span style={{
                fontSize: 12, fontWeight: active ? 700 : 500, fontFamily: T.fontSans,
                color: active ? T.tileAccent : (T.inkMuted || T.textSub),
              }}>{m.label}</span>
            </button>
          );
        })}
      </div>

      {/* ── Search Input (hidden when barcode active) ──────── */}
      {showSearch && (
        <div style={{ padding: "0 16px 10px" }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            background: T.surfaceLight || T.surface, borderRadius: T.radiusSm,
            padding: "0 14px",
          }}>
            <MagnifyingGlass size={18} weight="bold" color={T.inkFaint || T.textDim} style={{ flexShrink: 0 }} />
            <input
              ref={inputRef}
              value={nlpFood}
              onChange={e => handleInputChange(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") handleSubmit();
                if (e.key === "Escape") { setDbSuggestions([]); onClose(); }
              }}
              placeholder={searchMode === "branded" ? "Search branded foods..." : "Search whole foods..."}
              style={{
                flex: 1, background: "transparent", border: "none", outline: "none",
                padding: "13px 0", color: T.text, fontSize: 15,
                fontFamily: T.fontSans, fontWeight: 500,
              }}
            />
            {nlpFood && (
              <button onClick={() => { setNlpFood(""); setDbSuggestions([]); }} style={{
                background: T.surfaceMid || T.surface, border: "none", cursor: "pointer",
                width: 24, height: 24, borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <X size={12} weight="bold" color={T.inkMuted || T.textSub} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Scrollable Content Area ────────────────────────── */}
      <div style={{
        flex: 1, overflowY: "auto", padding: "0 16px 80px",
        WebkitOverflowScrolling: "touch",
      }}>
        {/* ── "Added" prompt ─────────────────────────────────── */}
        {addedPrompt && (
          <div style={{
            background: T.card, borderRadius: T.radius,
            padding: "20px", marginBottom: 12, marginTop: 4, textAlign: "center",
            boxShadow: T.shadowSm || "0 2px 8px rgba(0,0,0,0.06)",
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: "50%", background: `${T.accent}18`,
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 12px",
            }}>
              <Check size={20} weight="bold" color={T.accent} />
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, fontFamily: T.fontSans, color: T.text, display: "block", marginBottom: 4 }}>
              {addedPrompt.name}
            </span>
            <p style={{ fontSize: 12, color: T.inkMuted || T.textSub, fontFamily: T.fontSans, fontWeight: 500, margin: "0 0 16px" }}>
              Added to {addedPrompt.meal}
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleAddAnother} style={{
                flex: 1, padding: "12px", background: T.primary, border: "none",
                borderRadius: 12, cursor: "pointer",
                color: "#fff", fontSize: 13, fontWeight: 700, fontFamily: T.fontSans,
              }}>Add another</button>
              <button onClick={handleDone} style={{
                flex: 1, padding: "12px", background: T.surfaceLight || T.surface, border: "none",
                borderRadius: 12, cursor: "pointer",
                color: T.text, fontSize: 13, fontWeight: 600, fontFamily: T.fontSans,
              }}>Done</button>
            </div>
          </div>
        )}

        {/* ── Barcode Tab Content (Fix 1 — idle state moved here) ── */}
        {searchMode === "barcode" && !addedPrompt && (
          <div style={{ paddingTop: 8 }}>
            {/* Camera error (from scanner) */}
            {cameraErr && (
              <div style={{
                background: `${T.red}12`, borderRadius: 12,
                padding: "12px 14px", marginBottom: 12,
                display: "flex", gap: 10, alignItems: "center",
              }}>
                <X size={14} weight="bold" color={T.red} style={{ flexShrink: 0 }} />
                <p style={{ color: T.red, fontSize: 12, fontFamily: T.fontSans, fontWeight: 600, margin: 0, lineHeight: 1.5 }}>
                  {cameraErr}
                </p>
              </div>
            )}
            {/* Camera permission denied */}
            {permErr && (
              <div style={{
                background: `${T.red}12`, borderRadius: 12,
                padding: "12px 14px", marginBottom: 12,
                display: "flex", gap: 10, alignItems: "center",
              }}>
                <X size={14} weight="bold" color={T.red} style={{ flexShrink: 0 }} />
                <p style={{ color: T.red, fontSize: 12, fontFamily: T.fontSans, fontWeight: 600, margin: 0, lineHeight: 1.5 }}>
                  {permErr}
                </p>
              </div>
            )}

            {/* HTTPS toast */}
            {cameraToast && (
              <div style={{
                background: `${T.orange}15`, borderRadius: 12,
                padding: "12px 14px", marginBottom: 12,
                display: "flex", gap: 10, alignItems: "center",
              }}>
                <Lightning size={14} weight="fill" color={T.orange} style={{ flexShrink: 0 }} />
                <p style={{ color: T.orange, fontSize: 11, fontFamily: T.fontSans, fontWeight: 600, margin: 0 }}>
                  {cameraToast}
                </p>
              </div>
            )}

            {/* Camera viewfinder — shown when scanning */}
            {(barcodeStep === "scanning" || cameraActive) ? (
              <div style={{ marginBottom: 14 }}>
                {/* Orange header bar */}
                <div style={{
                  background: T.orange, borderRadius: "14px 14px 0 0",
                  padding: "10px 14px", display: "flex", alignItems: "center", gap: 8,
                }}>
                  <Camera size={14} weight="fill" color="#fff" />
                  <span style={{ color: "#fff", fontSize: 12, fontFamily: T.fontSans, fontWeight: 700 }}>Scanner active</span>
                </div>
                <div style={{
                  position: "relative", background: "#fff",
                  overflow: "hidden",
                  border: `2px solid ${T.orange}`, borderTop: "none",
                  borderRadius: "0 0 14px 14px",
                }}>
                  <video ref={videoRef} muted playsInline autoPlay disablePictureInPicture
                    style={{ width: "100%", display: "block", maxHeight: 240, objectFit: "cover" }} />
                  {videoReady && videoRef && videoRef.current && videoRef.current.paused && (
                    <div style={{
                      position: "absolute", inset: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: "rgba(255,255,255,0.8)",
                    }}>
                      <button onClick={() => videoRef.current.play().catch(() => {})} style={{
                        background: T.orange, border: "none", borderRadius: 12,
                        padding: "12px 22px", cursor: "pointer",
                        color: "#fff", fontSize: 13,
                        fontFamily: T.fontSans, fontWeight: 700,
                        display: "flex", alignItems: "center", gap: 8,
                      }}>
                        <Camera size={16} weight="fill" /> Tap to start
                      </button>
                    </div>
                  )}
                  {/* Animated scan-line */}
                  <style>{`@keyframes scanline{0%{top:20%}50%{top:75%}100%{top:20%}}`}</style>
                  <div style={{
                    position: "absolute", left: "8%", right: "8%",
                    height: 2, background: T.orange,
                    boxShadow: `0 0 8px ${T.orange}`,
                    animation: "scanline 2s ease-in-out infinite",
                  }} />
                  {/* Orange corner markers */}
                  {[{top:8,left:8},{top:8,right:8},{bottom:8,left:8},{bottom:8,right:8}].map((pos,i) => (
                    <div key={i} style={{
                      position: "absolute", ...pos, width: 20, height: 20,
                      borderTop: i < 2 ? `3px solid ${T.orange}` : "none",
                      borderBottom: i >= 2 ? `3px solid ${T.orange}` : "none",
                      borderLeft: (i === 0 || i === 2) ? `3px solid ${T.orange}` : "none",
                      borderRight: (i === 1 || i === 3) ? `3px solid ${T.orange}` : "none",
                    }} />
                  ))}
                </div>
                {/* Cancel — closes scanner but keeps overlay open */}
                <button onClick={() => { stopCamera && stopCamera(); setBarcodeStep && setBarcodeStep("idle"); }}
                  style={{
                    width: "100%", background: "transparent",
                    border: `1px solid ${T.cardBorder}`, borderRadius: T.radiusSm,
                    padding: "10px 0", cursor: "pointer",
                    color: T.red, fontSize: 11, fontFamily: T.fontSans, fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    marginTop: 8,
                  }}>
                  <X size={12} weight="bold" /> Cancel
                </button>
              </div>
            ) : (
              <>
                {/* Primary scan button */}
                <button onClick={handleOpenScanner}
                  style={{
                    width: "100%", background: "#fff", border: `2px solid ${T.orange}`,
                    borderRadius: T.radius, padding: "24px 0", cursor: "pointer",
                    display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 14,
                    boxShadow: `0 4px 16px ${T.orange}20`,
                  }}>
                  <div style={{
                    width: 54, height: 54, borderRadius: "50%",
                    background: `${T.orange}15`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Camera size={28} weight="fill" color={T.orange} />
                  </div>
                  <span style={{
                    color: T.orange, fontSize: 16, fontFamily: T.fontDisplay,
                    fontWeight: 700,
                  }}>
                    Open scanner
                  </span>
                  <span style={{
                    color: T.inkMuted, fontSize: 11,
                    fontFamily: T.fontSans, fontWeight: 500,
                  }}>
                    Point your camera at any barcode
                  </span>
                </button>

                {/* Divider */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                  <div style={{ flex: 1, height: 1, background: T.surfaceMid || T.surface }} />
                  <span style={{
                    color: T.inkFaint || T.textDim, fontSize: 11,
                    fontFamily: T.fontSans, fontWeight: 600,
                  }}>or enter manually</span>
                  <div style={{ flex: 1, height: 1, background: T.surfaceMid || T.surface }} />
                </div>
              </>
            )}

            {/* Manual entry + footer — hidden while camera is scanning */}
            {!(barcodeStep === "scanning" || cameraActive) && (
              <>
                {/* Manual entry row */}
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <div style={{
                    flex: 1, display: "flex", alignItems: "center", gap: 10,
                    background: T.surfaceLight || T.surface,
                    border: `1px solid ${T.cardBorder}`, borderRadius: 12,
                    padding: "0 14px",
                  }}>
                    <Barcode size={18} weight="bold" color={T.inkFaint || T.textDim} style={{ flexShrink: 0 }} />
                    <input value={barcode || ""} onChange={e => setBarcode(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && handleManualBarcode()}
                      placeholder="Enter barcode number..."
                      type="number"
                      style={{
                        flex: 1, background: "transparent", border: "none", outline: "none",
                        padding: "13px 0", color: T.text, fontSize: 15,
                        fontFamily: T.fontSans, fontWeight: 500,
                      }} />
                  </div>
                  <button onClick={handleManualBarcode}
                    disabled={logLoading || !(barcode && barcode.trim())}
                    style={{
                      background: T.orange, border: "none", borderRadius: 12,
                      padding: "0 16px", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      opacity: (logLoading || !(barcode && barcode.trim())) ? 0.4 : 1,
                    }}>
                    <CaretRight size={18} weight="bold" color="#fff" />
                  </button>
                </div>

                <p style={{
                  color: T.inkFaint || T.textDim, fontSize: 10,
                  fontFamily: T.fontSans, fontWeight: 500, margin: "0 0 0 4px",
                  lineHeight: 1.6,
                }}>
                  Open Food Facts database
                </p>
              </>
            )}

            {/* Product review — renders inside overlay when scan completes (Fix 1) */}
            {barcodeStep === "review" && scannedProduct && (
              <div style={{ marginTop: 8 }}>
                {/* Header */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: T.radiusSm,
                    background: T.surfaceLight, border: `1px solid ${T.cardBorder}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 16, flexShrink: 0,
                  }}>📦</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ color: T.inkFaint, fontSize: 10, fontFamily: T.fontSans, fontWeight: 600, margin: 0 }}>Product found</p>
                    <p style={{ color: T.text, fontSize: 14, fontFamily: T.fontSans, fontWeight: 700, margin: "2px 0 0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {scannedProduct.name}
                    </p>
                  </div>
                </div>
                {/* Per-100g macro grid */}
                <div style={{ display: "flex", background: T.surfaceLight, borderRadius: 12, overflow: "hidden", marginBottom: 12, border: `1px solid ${T.cardBorder}` }}>
                  {[
                    { label: "Kcal", val: Math.round(scannedProduct.kcal100),               color: T.orange  },
                    { label: "P",    val: `${scannedProduct.p100.toFixed(1)}g`,              color: "#E74C3C" },
                    { label: "C",    val: `${scannedProduct.c100.toFixed(1)}g`,              color: "#3498DB" },
                    { label: "F",    val: `${scannedProduct.f100.toFixed(1)}g`,              color: "#9B59B6" },
                  ].map((m, i) => (
                    <div key={m.label} style={{ flex: 1, textAlign: "center", padding: "8px 2px", borderLeft: i > 0 ? `1px solid ${T.cardBorder}` : "none" }}>
                      <p style={{ color: T.inkFaint, fontSize: 8, fontFamily: T.fontSans, margin: 0, fontWeight: 600 }}>{m.label}</p>
                      <p style={{ color: m.color, fontSize: 16, fontWeight: 800, fontFamily: T.fontSans, margin: "2px 0 0" }}>{m.val}</p>
                      <p style={{ color: T.inkFaint, fontSize: 8, fontFamily: T.fontSans, fontWeight: 500, margin: 0 }}>per 100{barcodeUnit}</p>
                    </div>
                  ))}
                </div>
                {/* Unit / Amount / Servings */}
                <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 12 }}>
                  <div style={{ flexShrink: 0 }}>
                    <label style={{ color: T.inkMuted, fontSize: 10, fontFamily: T.fontSans, fontWeight: 600, display: "block", marginBottom: 4 }}>Unit</label>
                    <div style={{ display: "flex", border: `1px solid ${T.cardBorder}`, borderRadius: T.radiusSm, overflow: "hidden", height: 44 }}>
                      {["g", "ml"].map((u, i) => (
                        <button key={u} onClick={() => setBarcodeUnit(u)} style={{
                          padding: "0 14px", background: barcodeUnit === u ? T.tileBg : T.surfaceLight,
                          border: "none", borderRight: i === 0 ? `1px solid ${T.cardBorder}` : "none",
                          color: barcodeUnit === u ? T.tileAccent : T.inkMuted,
                          cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: T.fontSans,
                          height: "100%",
                        }}>{u}</button>
                      ))}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ color: T.inkMuted, fontSize: 10, fontFamily: T.fontSans, fontWeight: 600, display: "block", marginBottom: 4 }}>Amount ({barcodeUnit})</label>
                    <input type="number" value={barcodeQty} onChange={e => setBarcodeQty(e.target.value)}
                      style={{
                        width: "100%", background: T.surfaceLight, border: `1px solid ${T.cardBorder}`,
                        borderRadius: T.radiusSm, padding: "10px 12px", color: T.text, fontSize: 15,
                        fontFamily: T.fontSans, fontWeight: 700, outline: "none",
                        boxSizing: "border-box", height: 44,
                      }} />
                  </div>
                  <div style={{ width: 72, flexShrink: 0 }}>
                    <label style={{ color: T.inkMuted, fontSize: 10, fontFamily: T.fontSans, fontWeight: 600, display: "block", marginBottom: 4 }}>× servings</label>
                    <input type="number" step="0.5" min="0.5" value={barcodeServings} onChange={e => setBarcodeServings(e.target.value)}
                      style={{
                        width: "100%", background: T.surfaceLight, border: `1px solid ${T.cardBorder}`,
                        borderRadius: T.radiusSm, padding: "10px 8px", color: T.text, fontSize: 15,
                        fontFamily: T.fontSans, fontWeight: 700, outline: "none",
                        boxSizing: "border-box", height: 44, textAlign: "center",
                      }} />
                  </div>
                </div>
                {/* Calorie summary */}
                {(() => {
                  const qty = parseFloat(barcodeQty) || 100, servMult = parseFloat(barcodeServings) || 1;
                  const s = (qty / 100) * servMult;
                  return (
                    <div style={{
                      background: T.surfaceLight, border: `1px solid ${T.cardBorder}`,
                      borderRadius: 12, padding: "12px 14px", marginBottom: 14, textAlign: "center",
                    }}>
                      <p style={{ margin: 0, fontSize: 24, fontWeight: 800, fontFamily: T.fontSans, color: T.orange, lineHeight: 1.1 }}>
                        {Math.round(scannedProduct.kcal100 * s)} <span style={{ fontSize: 13, fontWeight: 600, color: T.inkMuted }}>kcal</span>
                      </p>
                      <p style={{ margin: "6px 0 0", fontSize: 12, fontFamily: T.fontSans, fontWeight: 600, color: T.inkMuted, lineHeight: 1 }}>
                        P: {(scannedProduct.p100 * s).toFixed(1)}g · C: {(scannedProduct.c100 * s).toFixed(1)}g · F: {(scannedProduct.f100 * s).toFixed(1)}g
                      </p>
                    </div>
                  );
                })()}
                {/* Action buttons */}
                <button onClick={() => { confirmLog(barcodeServings); setBarcodeServings("1"); setBarcodeStep("idle"); setScannedProduct(null); }}
                  disabled={logLoading}
                  style={{
                    width: "100%", background: T.orange, border: "none", borderRadius: T.radiusSm,
                    padding: "15px 0", cursor: "pointer",
                    color: "#fff", fontSize: 15, fontFamily: T.fontDisplay, fontWeight: 800,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    marginBottom: 10, opacity: logLoading ? 0.6 : 1,
                  }}>
                  <Check size={16} weight="bold" /> Add to log
                </button>
                <button onClick={() => { setScannedProduct(null); setBarcodeStep("idle"); setBarcode(""); }}
                  style={{
                    width: "100%", background: "transparent", border: "none", cursor: "pointer",
                    color: T.inkMuted, fontSize: 12, fontFamily: T.fontSans, fontWeight: 600,
                    padding: "8px 0",
                  }}>
                  ← Scan again
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Yesterday Tab Content ─────────────────────────── */}
        {searchMode === "yesterday" && !addedPrompt && (
          <div>
            {yesterdayLoading ? (
              <div style={{ textAlign: "center", padding: "50px 20px", color: T.inkMuted || T.textDim, fontSize: 13, fontFamily: T.fontSans, fontWeight: 500 }}>
                <p style={{ margin: 0 }}>Loading yesterday's meals…</p>
              </div>
            ) : yesterdayLogs.length === 0 ? (
              <div style={{ textAlign: "center", padding: "50px 20px" }}>
                <Clock size={36} weight="light" color={T.inkFaint || T.textDim} style={{ marginBottom: 14, opacity: 0.3 }} />
                <p style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 700, fontFamily: T.fontSans, color: T.inkDark || T.text }}>Nothing logged yesterday</p>
                <p style={{ margin: 0, fontSize: 12, fontFamily: T.fontSans, fontWeight: 500, color: T.inkFaint || T.textDim, lineHeight: 1.6 }}>Your meals from yesterday will appear here</p>
              </div>
            ) : (
              <>
                {/* Select all toggle */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.inkMuted || T.textSub, fontFamily: T.fontSans }}>
                    {yesterdayLogs.length} item{yesterdayLogs.length !== 1 ? "s" : ""}
                  </span>
                  <button onClick={handleSelectAllYesterday} style={{
                    background: "none", border: "none", cursor: "pointer",
                    color: T.accent, fontSize: 12, fontWeight: 700, fontFamily: T.fontSans, padding: "4px 0",
                  }}>
                    {selectedIds.size === yesterdayLogs.length ? "Deselect all" : "Select all"}
                  </button>
                </div>
                {/* Items grouped by meal type */}
                {MEALS.filter(mt => yesterdayLogs.some(l => l.meal_type === mt)).map(mt => (
                  <div key={mt} style={{ marginBottom: 16 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: T.inkMuted || T.textSub, fontFamily: T.fontSans, letterSpacing: "0.5px", margin: "0 0 8px 2px" }}>{mt}</p>
                    {yesterdayLogs.filter(l => l.meal_type === mt).map(item => {
                      const sel = selectedIds.has(item.id);
                      return (
                        <button key={item.id} onClick={() => toggleYesterdayItem(item.id)} style={{
                          display: "flex", width: "100%", textAlign: "left", alignItems: "center", gap: 10,
                          background: sel ? `${T.accent}14` : (T.card || T.surface),
                          border: `1.5px solid ${sel ? T.accent : (T.cardBorder || T.border)}`,
                          borderRadius: T.radiusSm, padding: "12px 12px",
                          cursor: "pointer", marginBottom: 6, transition: "all 0.12s",
                        }}>
                          <div style={{
                            width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                            background: sel ? T.accent : "transparent",
                            border: `2px solid ${sel ? T.accent : (T.cardBorder || T.border)}`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            {sel && <Check size={11} weight="bold" color="#fff" />}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: T.fontSans, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</span>
                            {item.logged_grams > 0 && (
                              <span style={{ fontSize: 11, fontWeight: 500, color: T.inkMuted || T.textSub, fontFamily: T.fontSans }}>{item.logged_grams}g</span>
                            )}
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 800, color: T.text, fontFamily: T.fontDisplay, flexShrink: 0 }}>
                            {Math.round(item.calories)}<span style={{ fontSize: 10, fontWeight: 500, color: T.inkMuted || T.textSub }}> kcal</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* ── Loading indicator ──────────────────────────────── */}
        {dbSearching && !addedPrompt && searchMode !== "barcode" && searchMode !== "yesterday" && (
          <div style={{
            padding: "16px 0", textAlign: "center",
            fontSize: 12, fontFamily: T.fontSans, color: T.inkMuted || T.textDim, fontWeight: 600,
          }}>Searching...</div>
        )}

        {/* ── Recent Searches ──────────────────────────────── */}
        {showRecent && (
          <div style={{ marginBottom: 16, marginTop: 4 }}>
            <p style={{
              fontSize: 12, fontWeight: 600, color: T.inkMuted || T.textSub,
              fontFamily: T.fontSans, margin: "0 0 10px 2px",
            }}>Recent searches</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {recentItems.map((h, i) => (
                <button key={h.id || i} onClick={() => handleHistoryTap(h.query || h)} style={{
                  background: T.card, border: "none", borderRadius: T.radiusSm,
                  padding: "8px 14px", cursor: "pointer",
                  color: T.text, fontSize: 12, fontWeight: 600, fontFamily: T.fontSans,
                  boxShadow: T.shadowXs || "0 1px 4px rgba(0,0,0,0.04)",
                  transition: "all 0.1s",
                }}>{typeof h === "string" ? h : h.query}</button>
              ))}
            </div>
          </div>
        )}

        {/* ── Results (grouped by base food name) ────────── */}
        {groupedResults.length > 0 && !addedPrompt && searchMode !== "barcode" && searchMode !== "yesterday" && (
          <div>
            <p style={{
              fontSize: 12, fontWeight: 600, color: T.inkMuted || T.textSub,
              fontFamily: T.fontSans, margin: "4px 0 10px 2px",
            }}>{groupedResults.length} result{groupedResults.length !== 1 ? "s" : ""}</p>
            {groupedResults.map((group, i) => {
              const row = group.primary;
              const hasVariants = group.variants.length > 1;
              return (
              <button key={i} onClick={() => handleSelectResult(group)} style={{
                display: "block", width: "100%", textAlign: "left",
                background: T.card, border: "none", borderRadius: T.radiusSm,
                padding: "14px 14px 12px", marginBottom: 8, cursor: "pointer",
                boxShadow: T.shadowXs || "0 1px 4px rgba(0,0,0,0.04)",
                transition: "transform 0.1s",
              }}>
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{
                      fontSize: 14, fontWeight: 700, fontFamily: T.fontSans,
                      color: T.text,
                      display: "block", lineHeight: 1.3,
                    }}>{row.brand ? row.name : getBaseName(row.name)}</span>
                    {row.brand && (
                      <span style={{
                        fontSize: 11, fontWeight: 500, fontFamily: T.fontSans,
                        color: T.inkMuted || T.textSub, marginTop: 2, display: "block",
                      }}>{row.brand}</span>
                    )}
                    {hasVariants && !row.brand && (
                      <span style={{
                        fontSize: 10, fontWeight: 600, fontFamily: T.fontSans,
                        color: T.primary, marginTop: 3, display: "block",
                      }}>{group.variants.map(v => getVariantLabel(v.name)).join(' · ')}</span>
                    )}
                  </div>
                  <span style={{
                    fontSize: 15, fontWeight: 800, fontFamily: T.fontSans,
                    color: T.text, flexShrink: 0, marginLeft: 12,
                  }}>{Math.round(row.kcal100)}<span style={{ fontSize: 10, fontWeight: 500, color: T.inkMuted || T.textSub }}> kcal</span></span>
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                  {[
                    { label: "P", val: row.p100, color: T.macroProtein },
                    { label: "C", val: row.c100, color: T.macroCarbs },
                    { label: "F", val: row.f100, color: T.macroFat },
                  ].map(m => (
                    <span key={m.label} style={{
                      fontSize: 10, fontWeight: 700, fontFamily: T.fontSans,
                      color: m.color, background: `${m.color}12`,
                      padding: "4px 10px", borderRadius: 8,
                    }}>{m.label} {m.val}g</span>
                  ))}
                  <span style={{
                    fontSize: 10, fontWeight: 500, fontFamily: T.fontSans,
                    color: T.inkFaint || T.textDim, alignSelf: "center", marginLeft: "auto",
                  }}>per 100g</span>
                </div>
              </button>
              );
            })}
          </div>
        )}

        {/* ── Empty state ──────────────────────────────────── */}
        {searchMode !== "barcode" && searchMode !== "yesterday" && !showRecent && !addedPrompt && dbSuggestions.length === 0 && !dbSearching && nlpFood.length >= 2 && (
          <div style={{
            textAlign: "center", padding: "40px 20px",
            color: T.inkMuted || T.textDim, fontSize: 13, fontFamily: T.fontSans, fontWeight: 500,
          }}>
            <MagnifyingGlass size={32} weight="light" color={T.inkFaint || T.textDim} style={{ marginBottom: 12, opacity: 0.4 }} />
            <p style={{ margin: 0 }}>No results found</p>
            <p style={{ margin: "8px 0 0", fontSize: 12, color: T.inkFaint || T.textDim }}>
              Press Enter to search online database
            </p>
          </div>
        )}

        {/* ── Initial state ────────────────────────────────── */}
        {searchMode !== "barcode" && searchMode !== "yesterday" && !showRecent && !addedPrompt && dbSuggestions.length === 0 && !dbSearching && nlpFood.length < 2 && (
          <div style={{
            textAlign: "center", padding: "50px 20px",
            color: T.inkMuted || T.textDim, fontSize: 13, fontFamily: T.fontSans, fontWeight: 500,
          }}>
            <MagnifyingGlass size={36} weight="light" color={T.inkFaint || T.textDim} style={{ marginBottom: 14, opacity: 0.3 }} />
            <p style={{ margin: 0, fontSize: 14 }}>Search for any food</p>
            <p style={{ margin: "8px 0 0", fontSize: 12, lineHeight: 1.6, color: T.inkFaint || T.textDim }}>
              Type at least 2 characters to search<br />
              Press Enter for AI-powered lookup
            </p>
          </div>
        )}
      </div>

      {/* ── Yesterday: sticky Copy Selected bar ──────────── */}
      {searchMode === "yesterday" && selectedIds.size > 0 && (
        <div style={{
          padding: "12px 16px",
          borderTop: `1px solid ${T.cardBorder || T.border}`,
          background: T.pageBg || T.bg,
        }}>
          <button onClick={handleCopySelected} disabled={copyingYesterday} style={{
            width: "100%", padding: 15,
            background: T.actionPrimary || T.primary,
            border: "none", borderRadius: T.radius, cursor: "pointer",
            color: "#fff", fontSize: 15, fontFamily: T.fontDisplay, fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            opacity: copyingYesterday ? 0.6 : 1,
          }}>
            <Check size={16} weight="bold" />
            {copyingYesterday
              ? "Copying…"
              : `Copy ${selectedIds.size} item${selectedIds.size !== 1 ? "s" : ""} to today`}
          </button>
        </div>
      )}
    </div>
  );
}
